import React, { useEffect } from "react";
import {
  Dimensions,
  Modal,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
} from "react-native";
import { View } from "../../../components/shared/Themed";
import { useDispatch, useSelector } from "react-redux";
import Spinner from "../../../components/shared/Spinner";
import { getAllChinaTrackerDetailsWithShipments } from "../../../redux/dispatcher/chinaTrackerDispatcher";
import { useToast } from "react-native-toast-notifications";
import { formatLocalDate } from "../../../utils/common";

interface ChinaShipmentsListModalProps {
  visible: boolean;
  onClose: () => void;
  order: any;
}

// ─── Reusable Badge ──────────────────────────────────────────────────────────
const StatusBadge = ({ value }: { value?: any }) => {
  if (!value) return <Text style={styles.cellText}>—</Text>;
  return (
    <View style={styles.badge}>
      <Text style={styles.badgeText}>{value}</Text>
    </View>
  );
};

// ─── Table primitives ────────────────────────────────────────────────────────
const TableHeader = ({ columns }: { columns: string[] }) => (
  <View style={styles.tableHeaderRow}>
    {columns.map((col, i) => (
      <Text
        key={i}
        style={[styles.tableHeaderCell, i === 0 && styles.firstCell]}
      >
        {col}
      </Text>
    ))}
  </View>
);

const TableRow = ({
  cells,
  zebra,
}: {
  cells: (string | undefined | null | number)[];
  zebra: boolean;
}) => (
  <View style={[styles.tableRow, zebra && styles.tableRowZebra]}>
    {cells.map((cell, i) => (
      <Text
        key={i}
        style={[
          styles.cellText,
          i === 0 && styles.firstCell,
          i === 0 && styles.cellBold,
        ]}
      >
        {cell ?? "—"}
      </Text>
    ))}
  </View>
);

// ─── Section card wrapper ─────────────────────────────────────────────────────
const SectionCard = ({
  icon,
  title,
  accent,
  children,
}: {
  icon: string;
  title: string;
  accent?: string;
  children: React.ReactNode;
}) => (
  <View style={[styles.sectionCard, accent ? { borderLeftColor: accent } : {}]}>
    <View style={styles.sectionCardHeader}>
      <Text style={styles.sectionCardIcon}>{icon}</Text>
      <Text style={[styles.sectionCardTitle, accent ? { color: accent } : {}]}>
        {title}
      </Text>
    </View>
    {children}
  </View>
);

// ─── Main Modal ───────────────────────────────────────────────────────────────
const ChinaShipmentsListModal: React.FC<ChinaShipmentsListModalProps> = ({
  visible,
  onClose,
  order,
}) => {
  const dispatch = useDispatch<any>();
  const { orderDetailShipments, loadingShipmentsAction } = useSelector(
    (state: any) => state.chinaTracker,
  );
  const toast = useToast();

  useEffect(() => {
    if (order) {
      dispatch(
        getAllChinaTrackerDetailsWithShipments({
          orderNumber: order.OrderNumber1,
          key: order.Key,
        }),
      )
        .unwrap()
        .catch((err: any) => {
          toast.show(err, { type: "danger" });
        });
    }
  }, [order]);

  return (
    <Modal visible={visible} transparent animationType="fade">
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          {/* ── Header ─────────────────────────────────────── */}
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <TouchableOpacity onPress={onClose} style={styles.backButton}>
                <Text style={styles.backArrow}>‹</Text>
              </TouchableOpacity>
              <View>
                <Text style={styles.title}>
                  Order{" "}
                  <Text style={styles.titleAccent}>#{order?.OrderNumber1}</Text>
                </Text>
                {order?.Key && (
                  <Text style={styles.subtitle}>Key: {order.Key}</Text>
                )}
              </View>
            </View>
            <TouchableOpacity onPress={onClose} style={styles.closeBtn}>
              <Text style={styles.closeBtnText}>✕</Text>
            </TouchableOpacity>
          </View>

          {/* ── Body ───────────────────────────────────────── */}
          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
          >
            {loadingShipmentsAction ? (
              <View style={styles.spinnerWrap}>
                <Spinner
                  size="small"
                  color="#2563EB"
                  styles={{ marginTop: 50, marginBottom: 50 }}
                />
              </View>
            ) : (
              <>
                {/* ── Order Summary Table ─────────────────── */}
                <SectionCard icon="📋" title="Order Summary" accent="#2563EB">
                  <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                    <View style={styles.tableWrapper}>
                      <TableHeader columns={["Field", "Value"]} />
                      <TableRow
                        zebra={false}
                        cells={["Order Number", order?.OrderNumber1]}
                      />
                      <TableRow zebra={true} cells={["Key", order?.Key]} />
                      <TableRow
                        zebra={false}
                        cells={["Priority", order?.PriorityOrder || "—"]}
                      />
                    </View>
                  </ScrollView>
                </SectionCard>

                {/* ── Run Orders ─────────────────────────── */}
                {orderDetailShipments && orderDetailShipments.length > 0 ? (
                  orderDetailShipments.map((detail: any, idx: number) => (
                    <View key={detail._id} style={styles.runBlock}>
                      {/* Run order header pill */}
                      <View style={styles.runPill}>
                        <Text style={styles.runPillIcon}>🏭</Text>
                        <Text style={styles.runPillText}>
                          Run Order #{detail.runOrder}
                        </Text>
                        <View style={styles.runPillDivider} />
                        <Text style={styles.runPillSub}>
                          {detail.allocateProductionLine || "No Line"}
                        </Text>
                      </View>

                      {/* Run details table */}
                      <SectionCard
                        icon=""
                        title="Production Details"
                        accent="#0F9D58"
                      >
                        <ScrollView
                          horizontal
                          showsHorizontalScrollIndicator={true}
                        >
                          <View style={styles.tableWrapper}>
                            <TableHeader
                              columns={[
                                "Field",
                                "Planned Qty",
                                "Production Line",
                                "Plan Start",
                                "Est. Completion",
                                "ETD",
                                "ETA",
                                "Expected Arrival",
                              ]}
                            />
                            <TableRow
                              zebra={false}
                              cells={[
                                "Value",
                                detail.plannedQty,
                                detail.allocateProductionLine,
                                formatLocalDate(detail.planStartDay),
                                formatLocalDate(detail.ESTCompletionDate),
                                formatLocalDate(detail.ETD),
                                formatLocalDate(detail.ETA),
                                formatLocalDate(detail.ExpectedArrival),
                              ]}
                            />
                          </View>
                        </ScrollView>
                      </SectionCard>

                      {/* Shipments table */}
                      {detail.shipments && detail.shipments.length > 0 ? (
                        <SectionCard
                          icon="🚢"
                          title={`Shipments (${detail.shipments.length})`}
                          accent="#F59E0B"
                        >
                          <ScrollView
                            horizontal
                            showsHorizontalScrollIndicator={false}
                          >
                            <View style={styles.tableWrapper}>
                              <TableHeader
                                columns={[
                                  "Shipment No",
                                  "Qty",
                                  "Container",
                                  "ETD",
                                  "ETA",
                                ]}
                              />
                              {detail.shipments.map(
                                (item: any, sIdx: number) => (
                                  <TableRow
                                    key={item._id}
                                    zebra={sIdx % 2 === 1}
                                    cells={[
                                      item.shipmentNo,
                                      item.shipmentQty,
                                      item.containerNo,
                                      formatLocalDate(item.ETD),
                                      formatLocalDate(item.ETA),
                                    ]}
                                  />
                                ),
                              )}
                            </View>
                          </ScrollView>
                        </SectionCard>
                      ) : (
                        <View style={styles.emptyShipments}>
                          <Text style={styles.emptyShipmentsText}>
                            No shipments recorded for this run order.
                          </Text>
                        </View>
                      )}
                    </View>
                  ))
                ) : (
                  <View style={styles.emptyState}>
                    <Text style={styles.emptyStateIcon}>📦</Text>
                    <Text style={styles.emptyStateText}>
                      No factory order details found.
                    </Text>
                  </View>
                )}
              </>
            )}
          </ScrollView>

          {/* ── Footer ─────────────────────────────────────── */}
          <View style={styles.footer}>
            <TouchableOpacity onPress={onClose} style={styles.footerCloseBtn}>
              <Text style={styles.footerCloseBtnText}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const { width, height } = Dimensions.get("window");
const isWeb = Platform.OS === "web";

const COL_WIDTH = 140;
const FIRST_COL_WIDTH = 160;

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(15, 23, 42, 0.55)",
    justifyContent: "center",
    alignItems: "center",
    padding: isWeb ? 24 : 12,
  },
  modalContainer: {
    backgroundColor: "#FFFFFF",
    width: isWeb ? Math.min(width * 0.82, 960) : "96%",
    maxHeight: height * 0.9,
    borderRadius: 16,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.22,
    shadowRadius: 24,
    elevation: 12,
    display: "flex",
    flexDirection: "column",
  },

  // ── Header
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    backgroundColor: "#F8FAFC",
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  backButton: {
    width: 34,
    height: 34,
    borderRadius: 8,
    backgroundColor: "#EEF2FF",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 4,
  },
  backArrow: {
    fontSize: 22,
    color: "#2563EB",
    lineHeight: 26,
    fontWeight: "600",
  },
  title: {
    fontSize: 16,
    fontWeight: "700",
    color: "#111827",
  },
  titleAccent: {
    color: "#2563EB",
  },
  subtitle: {
    fontSize: 12,
    color: "#6B7280",
    marginTop: 1,
  },
  closeBtn: {
    width: 30,
    height: 30,
    borderRadius: 6,
    backgroundColor: "#F1F5F9",
    justifyContent: "center",
    alignItems: "center",
  },
  closeBtnText: {
    fontSize: 13,
    color: "#6B7280",
    fontWeight: "600",
  },

  // ── Scroll
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 8,
    gap: 0,
  },
  spinnerWrap: {
    justifyContent: "center",
    alignItems: "center",
    padding: 40,
  },

  // ── Section Card
  sectionCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderLeftWidth: 4,
    borderLeftColor: "#2563EB",
    marginBottom: 12,
    overflow: "hidden",
  },
  sectionCardHeader: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#F3F4F6",
    backgroundColor: "#F8FAFC",
    gap: 6,
  },
  sectionCardIcon: {
    fontSize: 14,
  },
  sectionCardTitle: {
    fontSize: 13,
    fontWeight: "700",
    color: "#374151",
    letterSpacing: 0.2,
  },

  // ── Table
  tableWrapper: {
    minWidth: "100%",
  },
  tableHeaderRow: {
    flexDirection: "row",
    backgroundColor: "#F1F5F9",
    borderBottomWidth: 1,
    borderBottomColor: "#CBD5E1",
  },
  tableHeaderCell: {
    width: COL_WIDTH,
    paddingVertical: 8,
    paddingHorizontal: 12,
    fontSize: 11,
    fontWeight: "700",
    color: "#4B5563",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#F3F4F6",
    backgroundColor: "#FFFFFF",
  },
  tableRowZebra: {
    backgroundColor: "#F9FAFB",
  },
  cellText: {
    width: COL_WIDTH,
    paddingVertical: 9,
    paddingHorizontal: 12,
    fontSize: 12,
    color: "#374151",
  },
  firstCell: {
    width: FIRST_COL_WIDTH,
  },
  cellBold: {
    fontWeight: "600",
    color: "#1F2937",
  },

  // ── Badge
  badge: {
    alignSelf: "flex-start",
    backgroundColor: "#DBEAFE",
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  badgeText: {
    fontSize: 11,
    color: "#1D4ED8",
    fontWeight: "600",
  },

  // ── Run block
  runBlock: {
    marginBottom: 16,
  },
  runPill: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#1E3A5F",
    borderRadius: 8,
    paddingVertical: 9,
    paddingHorizontal: 14,
    marginBottom: 10,
    gap: 8,
  },
  runPillIcon: {
    fontSize: 14,
  },
  runPillText: {
    fontSize: 13,
    fontWeight: "700",
    color: "#FFFFFF",
  },
  runPillDivider: {
    width: 1,
    height: 14,
    backgroundColor: "rgba(255,255,255,0.3)",
    marginHorizontal: 4,
  },
  runPillSub: {
    fontSize: 12,
    color: "rgba(255,255,255,0.75)",
  },

  // ── Empty states
  emptyShipments: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    backgroundColor: "#FFFBEB",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#FDE68A",
    marginBottom: 10,
  },
  emptyShipmentsText: {
    fontSize: 12,
    color: "#92400E",
    fontStyle: "italic",
  },
  emptyState: {
    alignItems: "center",
    paddingVertical: 48,
  },
  emptyStateIcon: {
    fontSize: 36,
    marginBottom: 10,
  },
  emptyStateText: {
    fontSize: 14,
    color: "#9CA3AF",
    fontStyle: "italic",
  },

  // ── Footer
  footer: {
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
    paddingHorizontal: 20,
    paddingVertical: 12,
    flexDirection: "row",
    justifyContent: "flex-end",
    backgroundColor: "#F8FAFC",
  },
  footerCloseBtn: {
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 8,
    paddingHorizontal: 20,
    paddingVertical: 8,
    backgroundColor: "#FFFFFF",
  },
  footerCloseBtnText: {
    fontSize: 13,
    fontWeight: "600",
    color: "#374151",
  },
});

export default ChinaShipmentsListModal;
